import { useContext, useEffect, useState } from "react";

import {
  Edit as EditIcon,
  Add,
  ArrowBack,
  Upgrade as UpgradeIcon,
  TextFormat as TextFormatIcon,
  ContentCopy as ContentCopyIcon,
  LibraryAddOutlined as LibraryAddIcon,
  FileUpload as FileUploadIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  listOrgItemTypeOptions,
  listSharedItemTypeOptions,
  duplicateItemTypeOption,
  postItemTypeOptionConvertV2,
  getItemTypeOption,
  postItemTypeOption,
} from "@rubi/api-generated/clientRs/sdk.gen";
import type {
  ListOrgItemTypeOptionsResponse,
  ListSharedItemTypeOptionsResponse,
  ItemTypeOptionSummary,
} from "@rubi/api-generated/clientRs/types.gen";
import { toast } from "@rubi/ui";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { LinkCell } from "@/common/DataGridCells";
import { Permission } from "@/components/UserManagement/types";
import { AuthStateContext } from "@/contexts/AuthContext";

import BulkRecaseDropdownsModal, {
  BulkRecaseTarget,
} from "./BulkRecaseDropdownsModal";
import ImportDropdownJsonModal from "./ImportDropdownJsonModal";

interface DropdownList {
  id: string;
  name: string;
  itemCount: number;
  translationCount: number;
  availableLanguages: string[];
  isLegacy: boolean;
}

const DropdownConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams<{ orgId: string }>();
  const [organizationLists, setOrganizationLists] = useState<DropdownList[]>(
    [],
  );
  const [sharedLists, setSharedLists] = useState<DropdownList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [convertTarget, setConvertTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [privateCopyingId, setPrivateCopyingId] = useState<string | null>(null);

  const { currentUser } = useContext(AuthStateContext);
  // Making a private copy of a shared list creates a new list, which the backend gates on report_v2.create_survey.
  const canManageLists =
    currentUser?.permissions?.some(
      (p) => p.title === Permission.REPORT_V2_CREATE_SURVEY,
    ) ?? false;

  // Targets for the bulk casing modal. Only org lists are shown; shared lists are
  // read-only and would just clutter the picker. Legacy v1 lists appear but are
  // disabled with an explanation.
  const bulkEditTargets: BulkRecaseTarget[] = organizationLists.map((list) => ({
    id: list.id,
    name: list.name,
    itemCount: list.itemCount,
    disabledReason: list.isLegacy
      ? "Legacy v1 list — convert to v2 first"
      : undefined,
  }));

  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";

  const fetchData = async () => {
    if (!orgId) return;

    try {
      setLoading(true);
      setError(null);

      const orgResponse = await listOrgItemTypeOptions({
        path: { org_id: orgId },
      });
      const orgData = orgResponse.data as ListOrgItemTypeOptionsResponse;

      const sharedResponse = await listSharedItemTypeOptions();
      const sharedData =
        sharedResponse.data as ListSharedItemTypeOptionsResponse;

      const transformData = (items: ItemTypeOptionSummary[]): DropdownList[] =>
        items.map((item) => ({
          id: item.id,
          name: item?.name || "",
          itemCount: item.itemCount || 0,
          translationCount: item.translationCount || 0,
          availableLanguages: item.availableLanguages || [],
          isLegacy: item.isLegacy || false,
        }));

      setOrganizationLists(transformData(orgData.itemTypeOptions));
      setSharedLists(transformData(sharedData.itemTypeOptions));
    } catch (err) {
      console.error("Failed to fetch item type options:", err);
      setError("Failed to load dropdown lists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const handleDuplicate = async (listId: string, listName: string) => {
    try {
      setDuplicatingId(listId);
      const response = await duplicateItemTypeOption({
        path: { item_type_option_id: listId },
        body: {},
      });
      const newName = response.data?.name ?? `${listName} (copy)`;
      toast.success(`Created "${newName}"`);
      await fetchData();
    } catch (err: any) {
      console.error("Failed to duplicate item type option:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to duplicate list";
      toast.error(message);
    } finally {
      setDuplicatingId(null);
    }
  };

  // Copy a shared (platform) list into the current org so it can be edited privately.
  const handleMakePrivateCopy = async (listId: string, listName: string) => {
    if (!orgId) return;
    try {
      setPrivateCopyingId(listId);
      // Read the shared list (v2 shape, legacy converted on the fly), then create
      // it under the user's own org.
      const sourceList = await getItemTypeOption({
        path: { item_type_option_id: listId },
      });
      const items = sourceList.data?.items;
      if (!items) throw new Error("Could not load the source list");

      const response = await postItemTypeOption({
        body: { rubi_org_id: orgId, name: listName || undefined, items },
      });
      const newName =
        response.data?.item_type_option?.name || listName || "list";
      toast.success(`Copied "${newName}" into your organization`);
      await fetchData();
    } catch (err: any) {
      console.error("Failed to make a private copy:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to make a private copy";
      toast.error(message);
    } finally {
      setPrivateCopyingId(null);
    }
  };

  const handleOpenConvertConfirm = (listId: string, listName: string) => {
    setConvertTarget({ id: listId, name: listName });
  };

  const handleCloseConvertConfirm = () => {
    if (convertingId) return;
    setConvertTarget(null);
  };

  const handleConvertToV2 = async () => {
    if (!convertTarget) return;
    const { id: listId, name: listName } = convertTarget;
    try {
      setConvertingId(listId);
      const response = await postItemTypeOptionConvertV2({
        path: { item_type_option_id: listId },
      });
      const newName = response.data?.name ?? `${listName} (v2)`;
      toast.success(`Created v2 copy "${newName}"`);
      setConvertTarget(null);
      await fetchData();
    } catch (err: any) {
      console.error("Failed to convert item type option to v2:", err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to convert list to v2";
      toast.error(message);
    } finally {
      setConvertingId(null);
    }
  };

  const handleView = (
    listId: string,
    listName: string,
    isReadOnly: boolean,
  ) => {
    navigate(`/${app}/${orgId}/forms/dropdowns/${listId}`, {
      state: { listName, isReadOnly },
    });
  };

  const handleEdit = (
    listId: string,
    listName: string,
    isReadOnly: boolean,
  ) => {
    navigate(`/${app}/${orgId}/forms/dropdowns/${listId}/edit`, {
      state: { listName, isReadOnly },
    });
  };

  const createNewHandler = () => {
    navigate(`/${app}/${orgId}/forms/dropdowns/new`);
  };

  const renderLinkCell = (value: string, row: any, isReadOnly: boolean) => {
    return (
      <LinkCell
        value={value}
        onClick={() => handleView(row.id, row.name, isReadOnly)}
      />
    );
  };

  const createColumns = (isReadOnly: boolean): GridColDef[] => {
    const baseColumns: GridColDef[] = [
      {
        field: "id",
        headerName: "List ID",
        flex: 0.3,
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ value, row }) => renderLinkCell(value, row, isReadOnly),
      },
      {
        field: "name",
        headerName: "List Name",
        flex: 1,
        minWidth: 200,
        sortable: false,
        filterable: false,
      },
      {
        field: "itemCount",
        headerName: "Items",
        flex: 0.2,
        minWidth: 80,
        sortable: false,
        filterable: false,
      },
      {
        field: "translationCount",
        headerName: "Translations",
        flex: 0.25,
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.translationCount === 0) {
            return "—";
          }
          return (
            <span title={row.availableLanguages?.join(", ") || ""}>
              {row.translationCount} language
              {row.translationCount !== 1 ? "s" : ""}
            </span>
          );
        },
      },
    ];

    // Only add actions column for organization lists (not shared lists)
    if (!isReadOnly) {
      baseColumns.push({
        field: "actions",
        headerName: "Actions",
        flex: 0.3,
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              onClick={() => handleEdit(row.id, row.name, isReadOnly)}
              size="small"
              aria-label={`Edit ${row.name}`}
            >
              <EditIcon />
            </IconButton>
            {!row.isLegacy && (
              <Tooltip title="Duplicate (copy in this org)">
                <span>
                  <IconButton
                    onClick={() => handleDuplicate(row.id, row.name)}
                    size="small"
                    disabled={duplicatingId === row.id}
                    aria-label={`Duplicate ${row.name}`}
                  >
                    {duplicatingId === row.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <ContentCopyIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {row.isLegacy && (
              <Tooltip title="Convert to v2 (creates a new list)">
                <span>
                  <IconButton
                    onClick={() => handleOpenConvertConfirm(row.id, row.name)}
                    size="small"
                    disabled={convertingId === row.id}
                    aria-label={`Convert ${row.name} to v2`}
                  >
                    {convertingId === row.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <UpgradeIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        ),
      });
    } else {
      // Shared lists are read-only, but list managers can pull one into their own org.
      // Legacy v1 lists are copyable too — the backend converts them to v2 on copy.
      baseColumns.push({
        field: "actions",
        headerName: "Actions",
        flex: 0.3,
        minWidth: 160,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) =>
          canManageLists ? (
            <Tooltip title="Make a private copy in your organization">
              <span>
                <IconButton
                  onClick={() => handleMakePrivateCopy(row.id, row.name)}
                  size="small"
                  disabled={privateCopyingId === row.id}
                  aria-label={`Make a private copy of ${row.name}`}
                >
                  {privateCopyingId === row.id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <LibraryAddIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null,
      });
    }

    return baseColumns;
  };

  const renderTable = (
    title: string,
    lists: DropdownList[],
    isReadOnly: boolean = false,
  ) => (
    <Box sx={{ width: "100%", mb: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {title}
      </Typography>
      <DataGrid
        rows={lists}
        columns={createColumns(isReadOnly)}
        autoHeight
        rowSelection={false}
        disableColumnMenu
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "none",
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            {
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: "inherit",
              display: "flex",
              alignItems: "center",
            },
        }}
        localeText={{
          noRowsLabel: `No ${isReadOnly ? "shared" : "organization"} lists found`,
        }}
      />
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const handleBack = () => {
    navigate(`/${app}/${orgId}/forms`);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Button onClick={handleBack} startIcon={<ArrowBack />} sx={{ mb: 2 }}>
        Back to Form Management
      </Button>

      <Grid container justifyContent="space-between" alignItems="center">
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="h4" gutterBottom>
            Dropdown Configurations
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} style={{ textAlign: "right" }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setBulkEditOpen(true)}
            startIcon={<TextFormatIcon />}
            sx={{ mr: 1 }}
          >
            Bulk Edit
          </Button>
          {canManageLists && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setImportOpen(true)}
              startIcon={<FileUploadIcon />}
              sx={{ mr: 1 }}
            >
              Import JSON
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={createNewHandler}
            startIcon={<Add />}
          >
            Create List
          </Button>
        </Grid>
      </Grid>

      <Typography
        variant="body1"
        paragraph
        sx={{ mt: 2, mb: 3, color: "text.secondary", lineHeight: 1.6 }}
      >
        Manage dropdown option lists that can be used in form questions.
        Organization lists are specific to your organization, while shared lists
        are available across the platform.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mt: 3 }}>
        {renderTable("Organization Lists", organizationLists)}
        {renderTable("Shared Lists", sharedLists, true)}
      </Box>

      <BulkRecaseDropdownsModal
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        orgId={orgId ?? ""}
        targets={bulkEditTargets}
        onSuccess={fetchData}
      />

      <ImportDropdownJsonModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        orgId={orgId ?? ""}
        onSuccess={fetchData}
      />

      <Dialog
        open={convertTarget !== null}
        onClose={handleCloseConvertConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Convert to v2?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            This will create a new v2 copy of{" "}
            <strong>{convertTarget?.name || "this list"}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The original v1 list is kept intact so any surveys still referencing
            it continue to work. You&apos;ll need to swap survey references over
            to the new list manually.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvertConfirm} disabled={!!convertingId}>
            Cancel
          </Button>
          <Button
            onClick={handleConvertToV2}
            variant="contained"
            color="primary"
            disabled={!!convertingId}
            startIcon={
              convertingId ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <UpgradeIcon />
              )
            }
          >
            {convertingId ? "Converting..." : "Convert to v2"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DropdownConfiguration;
